import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Chip,
  LinkButton,
  MetricTile,
  PageHeader,
  Skeleton,
  SkeletonRegion,
  StateBlock,
  type UrlTab,
  UrlTabs,
} from "../../components/ui";
import { PREVIEW_LANGUAGES } from "../../constants/languages";
import useAuth from "../../hooks/useAuth";
import { usePageTitle } from "../../hooks/usePageTitle";
import api from "../../services/api";
import type { PlanLimitKey, PlanUsage } from "../../types/Restaurant";
import type { User } from "../../types/User";
import type { LocationRecord, RestaurantRecord } from "../Locations/apiTypes";
import { useRequest } from "../Locations/useRequest";
import { Logo } from "./Logo";

const LIMIT_KEYS: PlanLimitKey[] = ["locations", "users", "languages"];

const Overview = ({
  restaurant,
  usage,
}: {
  restaurant: RestaurantRecord;
  usage: PlanUsage | null;
}) => {
  const { t, i18n } = useTranslation("locations");
  const { t: tv } = useTranslation("venue");
  const trial = restaurant.trialEndsAt
    ? new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "long",
        timeZone: "UTC",
      }).format(new Date(restaurant.trialEndsAt))
    : null;
  return (
    <div className="flex flex-col gap-6">
      <Card as="section" padding="lg">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-title text-strong">
            {t("restaurants.detail.plan")}
          </h2>
          <Chip variant="info" size="lg">
            {t(`common:plans.${restaurant.plan}`)}
          </Chip>
        </div>
        <p className="mt-1 text-small text-muted">
          {trial
            ? t("restaurants.detail.trialEnds", { date: trial })
            : t("restaurants.detail.noTrial")}
        </p>
        {usage && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {LIMIT_KEYS.map((key) => (
              <MetricTile
                key={key}
                label={t(`restaurants.detail.usage.${key}`)}
                value={
                  usage.limits[key] === null
                    ? usage.usage[key]
                    : `${usage.usage[key]} / ${usage.limits[key]}`
                }
                delta={
                  usage.limits[key] === null
                    ? t("restaurants.detail.unlimited")
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </Card>
      <Card as="section" padding="lg">
        <h2 className="text-title text-strong">
          {t("restaurants.detail.brand")}
        </h2>
        <div className="mt-4 flex flex-col gap-4 sm:flex-row">
          <Logo
            url={restaurant.logoUrl}
            name={restaurant.name}
            className="size-24"
          />
          <dl className="grid flex-1 gap-3 text-small sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-muted">
                {t("restaurants.form.description")}
              </dt>
              <dd className="text-strong">{restaurant.description || "—"}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("restaurants.form.languages")}</dt>
              <dd className="text-strong">
                {restaurant.supportedLanguages
                  .map(
                    (c) =>
                      PREVIEW_LANGUAGES.find((l) => l.code === c)
                        ?.originalLabel ?? c,
                  )
                  .join(", ")}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{tv("detail.timezone")}</dt>
              <dd className="text-strong">{restaurant.timezone}</dd>
            </div>
            <div>
              <dt className="text-muted">{t("restaurants.form.currency")}</dt>
              <dd className="text-strong">
                {restaurant.currency} ({restaurant.currencySymbol})
              </dd>
            </div>
          </dl>
        </div>
      </Card>
    </div>
  );
};

const LocationsPanel = ({ restaurantId }: { restaurantId: string }) => {
  const { t } = useTranslation("locations");
  const { data, state, retry } = useRequest(`r-locations:${restaurantId}`, () =>
    api
      .get<LocationRecord[]>(`/restaurants/${restaurantId}/locations`)
      .then((r) => r.data),
  );
  if (state === "error")
    return (
      <StateBlock
        variant="error"
        title={t("restaurants.detail.loadFailed")}
        action={<Button onClick={retry}>{t("common:ui.retry")}</Button>}
      />
    );
  if (!data)
    return (
      <SkeletonRegion>
        <Skeleton className="h-32" />
      </SkeletonRegion>
    );
  const add = (
    <LinkButton
      to={`/locations/create?restaurantId=${restaurantId}`}
      variant="primary"
      icon={<PlusIcon className="size-5" aria-hidden="true" />}
    >
      {t("list.add")}
    </LinkButton>
  );
  return data.length === 0 ? (
    <Card padding="none">
      <StateBlock
        variant="empty"
        title={t("list.emptyTitle")}
        body={t("list.emptyBody")}
        action={add}
      />
    </Card>
  ) : (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">{add}</div>
      <Card padding="none" className="overflow-hidden"><ul>
        {data.map((l) => (
          <li key={l.id} className="border-b border-hairline last:border-0">
            <Link
              to={`/locations/${l.id}`}
              className="flex min-h-14 flex-col justify-center px-4 py-3 hover:bg-hover sm:px-6"
            >
              <span className="font-semibold text-strong">{l.name}</span>
              <span className="text-small text-muted">{l.address}</span>
            </Link>
          </li>
        ))}
      </ul></Card>
    </div>
  );
};

const TeamPanel = ({ restaurantId }: { restaurantId: string }) => {
  const { t } = useTranslation("locations");
  const { data, state, retry } = useRequest(`r-team:${restaurantId}`, () =>
    api
      .get<User[]>("/users")
      .then((r) => r.data.filter((u) => u.restaurant?.id === restaurantId)),
  );
  if (state === "error")
    return (
      <StateBlock
        variant="error"
        title={t("restaurants.detail.loadFailed")}
        action={<Button onClick={retry}>{t("common:ui.retry")}</Button>}
      />
    );
  if (!data)
    return (
      <SkeletonRegion>
        <Skeleton className="h-32" />
      </SkeletonRegion>
    );
  return data.length === 0 ? (
    <Card padding="none">
      <StateBlock variant="empty" title={t("restaurants.detail.teamEmpty")} />
    </Card>
  ) : (
    <Card padding="none" className="overflow-hidden"><ul>
      {data.map((u) => (
        <li key={u.id} className="border-b border-hairline last:border-0">
          <Link
            to={`/users/edit/${u.id}`}
            className="flex min-h-14 items-center justify-between gap-3 px-4 py-3 hover:bg-hover sm:px-6"
          >
            <span className="min-w-0">
              <span className="block truncate font-semibold text-strong">
                {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}
              </span>
              <span className="block truncate text-small text-muted">
                {u.email}
              </span>
            </span>
            <Chip>{u.role.name}</Chip>
          </Link>
        </li>
      ))}
    </ul></Card>
  );
};

const RestaurantDetail = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { t } = useTranslation(["locations", "common"]);
  const { hasRole } = useAuth();
  const isAdmin = hasRole(["Admin"]);
  const { data, state, retry } = useRequest(`restaurant:${id}`, async () => {
    const [restaurant, usage] = await Promise.all([
      api.get<RestaurantRecord>(`/restaurants/${id}`).then((r) => r.data),
      api
        .get<PlanUsage>(`/restaurants/${id}/usage`)
        .then((r) => r.data)
        .catch(() => null),
    ]);
    return { restaurant, usage };
  });
  usePageTitle(data?.restaurant.name ?? t("restaurants.list.title"));

  if (state === "error") {
    return (
      <>
        <PageHeader
          title={t("restaurants.list.title")}
          back={
            isAdmin
              ? { to: "/restaurants", label: t("restaurants.list.title") }
              : undefined
          }
        />
        <StateBlock
          variant="error"
          title={t("restaurants.detail.loadFailed")}
          body={t("common:ui.checkConnection")}
          action={<Button onClick={retry}>{t("common:ui.retry")}</Button>}
        />
      </>
    );
  }
  if (!data) {
    return (
      <SkeletonRegion className="flex flex-col gap-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40" />
      </SkeletonRegion>
    );
  }
  const { restaurant, usage } = data;
  const tabs: UrlTab[] = [
    {
      id: "overview",
      label: t("restaurants.detail.tabs.overview"),
      panel: <Overview restaurant={restaurant} usage={usage} />,
    },
    {
      id: "locations",
      label: t("restaurants.detail.tabs.locations"),
      panel: <LocationsPanel restaurantId={id} />,
    },
    {
      id: "team",
      label: t("restaurants.detail.tabs.team"),
      panel: <TeamPanel restaurantId={id} />,
    },
  ];
  return (
    <>
      <PageHeader
        title={restaurant.name}
        meta={`${t(`common:plans.${restaurant.plan}`)} · ${restaurant.currency}`}
        back={
          isAdmin
            ? { to: "/restaurants", label: t("restaurants.list.title") }
            : undefined
        }
        actions={
          <LinkButton
            to={`/restaurants/edit/${id}`}
            variant="outline"
            icon={<PencilSquareIcon className="size-5" aria-hidden="true" />}
          >
            {t("common:ui.edit")}
          </LinkButton>
        }
      />
      <UrlTabs tabs={tabs} label={t("restaurants.detail.tabsLabel")} />
    </>
  );
};

export default RestaurantDetail;
